/**
 * The packet log: one dense clickable row per message.
 *
 * Hand-rolled rows rather than <Table>, following the Webhooks delivery log on
 * the sibling tab — rows here are dense, clickable and live-appending, which
 * table semantics fight.
 *
 * Raw ER7 is deliberately NOT rendered here. The list endpoint omits the bodies
 * anyway, and a wall of pipe-delimited text is unreadable at row density; it
 * belongs in the detail view.
 *
 * Every derived field goes through `lib/dataLayer/messageView`, because that is
 * where the `msh: null` branch is tested. Reading `message.msh!.type` directly
 * would reintroduce the crash those tests exist to prevent.
 */
import { Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  ackLabel,
  ackTone,
  formatBytes,
  isUnparsed,
  messageControlId,
  messageRoute,
  messageTypeLabel,
  packetTimestamp,
  sendOutcomeLabel,
  type AckTone,
} from '@/lib/dataLayer/messageView'
import type { Hl7MessageSummary } from '@/lib/dataLayer/types'

const TONE_DOT: Record<AckTone, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  grey: 'bg-muted-foreground/40',
}

export function PacketLog({
  messages,
  isLoading,
  emptyHint,
  onSelect,
  selectedId,
}: {
  messages: Hl7MessageSummary[]
  isLoading: boolean
  emptyHint: React.ReactNode
  onSelect: (message: Hl7MessageSummary) => void
  selectedId?: string | null
}) {
  if (isLoading && messages.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
        {emptyHint}
      </div>
    )
  }

  return (
    <div className="max-h-[28rem] divide-y divide-border/60 overflow-y-auto rounded-lg border border-border/60">
      {messages.map((message) => (
        <PacketRow
          key={message.id}
          message={message}
          selected={message.id === selectedId}
          onSelect={() => onSelect(message)}
        />
      ))}
    </div>
  )
}

function PacketRow({
  message,
  selected,
  onSelect,
}: {
  message: Hl7MessageSummary
  selected: boolean
  onSelect: () => void
}) {
  const unparsed = isUnparsed(message)
  const outcome = message.direction === 'out' ? sendOutcomeLabel(message) : null

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50',
        selected && 'bg-muted/70',
      )}
    >
      <span
        className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT[ackTone(message)])}
        title={message.parseError ?? ackLabel(message)}
      />

      {/* Wide enough for the full `2026-09-01 10:11:32.165`, and deliberately
          not truncated: a clipped timestamp is worse than no timestamp. */}
      <span className="w-48 shrink-0 font-mono text-xs text-muted-foreground" title={message.receivedAt}>
        {packetTimestamp(message.receivedAt)}
      </span>

      <span className="w-20 shrink-0 truncate font-mono text-xs text-muted-foreground">
        {message.channelId}
      </span>

      <span className="w-24 shrink-0">
        {unparsed ? (
          <Badge variant="destructive" className="text-[10px]">
            unparsed
          </Badge>
        ) : (
          <span className="font-mono text-xs">{messageTypeLabel(message)}</span>
        )}
      </span>

      <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">
        {unparsed ? message.parseError : `${messageControlId(message)} · ${messageRoute(message)}`}
      </span>

      {outcome && (
        <span className="shrink-0 truncate text-xs text-destructive" title={outcome}>
          {outcome}
        </span>
      )}

      {/* Injected traffic must never read as a real arrival. */}
      {message.mimic && (
        <Badge variant="secondary" className="shrink-0 text-[10px]">
          simulated
        </Badge>
      )}

      {message.typeMismatch && (
        <Badge variant="outline" className="shrink-0 text-[10px] text-amber-600">
          type mismatch
        </Badge>
      )}

      <span className="w-16 shrink-0 text-right font-mono text-[11px] text-muted-foreground">
        {formatBytes(message.bytes)}
      </span>
      <span className="w-12 shrink-0 text-right font-mono text-[11px] text-muted-foreground">
        {message.latencyMs}ms
      </span>
    </button>
  )
}
